// ============================================================
// SPRITE BROWSER SCENE
// 120-item grid showing sprites or glyphs for every word pair
// sharing a fixed first word. Toggle button swaps between views.
// Launched from dictionary screen wheel B.
// ============================================================

import Phaser from 'phaser';
import {
    GLYPH_COLS,
    GLYPH_ROWS,
    GLYPH_SIZE,
    GLYPH_BUFFER,
    SPECIAL_GLYPHS,
    getGlyphIndex,
    getGlyphCount,
    getGlyphColor,
    getWord,
    getCategory,
} from '../toki/TokiPonaData';
import { createPixelFont, PIXEL_FONT_KEY } from '../rendering/PixelFont';
import { DictionaryScene, SPRITE_MAP, DEFINITION_SPRITE_MAP, getMultiTileCreatureId } from './DictionaryScene';
import { isAtlasFrame, atlasApplyTexture } from '../rendering/AtlasUtils';

const PADDING = 4;
const BUTTON_SIZE = 16;
const MUN_INDEX = 58;

export class SpriteBrowserScene extends Phaser.Scene {
    private onSelect: ((index: number) => void) | null = null;
    private onClose: (() => void) | null = null;
    private wordA: number = 0;
    private mobile: boolean = false;

    // Grid items — swapped between glyph and sprite mode
    private gridItems: Phaser.GameObjects.Image[] = [];
    private spriteMode: boolean = false;

    // Toggle button elements (need updating on toggle)
    private toggleBg!: Phaser.GameObjects.Rectangle;
    private toggleIcon!: Phaser.GameObjects.Image;

    constructor() {
        super({ key: 'SpriteBrowserScene' });
    }

    init(data: {
        wordA?: number;
        mobile?: boolean;
        onSelect?: (index: number) => void;
        onClose?: () => void;
    }): void {
        this.wordA = data.wordA ?? 0;
        this.mobile = data.mobile ?? false;
        this.onSelect = data.onSelect || null;
        this.onClose = data.onClose || null;
    }

    create(): void {
        const { width, height } = this.cameras.main;

        createPixelFont(this);

        // Reset
        this.gridItems = [];
        this.spriteMode = false;

        // Semi-transparent overlay
        const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.9);
        overlay.setOrigin(0, 0).setDepth(0);

        // --- Header row ---

        // Toggle button (top-left) — colored background + "sitelen" glyph
        const sitelenIndex = getGlyphIndex('sitelen');
        this.toggleBg = this.add.rectangle(
            PADDING, PADDING, BUTTON_SIZE, BUTTON_SIZE, 0x444444
        );
        this.toggleBg.setOrigin(0, 0).setDepth(9);
        this.toggleBg.setStrokeStyle(1, 0x888888);
        this.toggleBg.setInteractive({ useHandCursor: true });
        this.toggleBg.on('pointerdown', () => {
            this.spriteMode = !this.spriteMode;
            this.updateToggleAppearance();
            this.updateGrid();
        });

        this.toggleIcon = this.add.image(
            PADDING + BUTTON_SIZE / 2, PADDING + BUTTON_SIZE / 2,
            'toki_glyphs', sitelenIndex
        );
        this.toggleIcon.setOrigin(0.5, 0.5);
        this.toggleIcon.setDisplaySize(BUTTON_SIZE - 4, BUTTON_SIZE - 4);
        this.toggleIcon.setDepth(10);
        this.toggleIcon.setTint(0xffffff);
        this.updateToggleAppearance();

        // Word A glyph (after toggle button)
        const glyphX = PADDING + BUTTON_SIZE + PADDING;
        const wordAGlyph = this.add.image(glyphX, PADDING, 'toki_glyphs', this.wordA);
        wordAGlyph.setOrigin(0, 0);
        wordAGlyph.setDisplaySize(BUTTON_SIZE, BUTTON_SIZE);
        wordAGlyph.setDepth(10);
        wordAGlyph.setTint(getGlyphColor(this.wordA));

        // Category name in unifont (after the word A glyph)
        const catX = glyphX + BUTTON_SIZE + PADDING;
        const catName = getCategory(this.wordA) || '?';
        const catLabel = this.add.bitmapText(catX, PADDING + 4, PIXEL_FONT_KEY, catName);
        catLabel.setOrigin(0, 0).setDepth(10);
        catLabel.setTint(0x888888);

        // Escape button (top-right)
        const alaIndex = getGlyphIndex(SPECIAL_GLYPHS.ESCAPE);
        const escBtn = this.add.image(
            width - PADDING - BUTTON_SIZE, PADDING,
            'toki_glyphs', alaIndex
        );
        escBtn.setOrigin(0, 0);
        escBtn.setDisplaySize(BUTTON_SIZE, BUTTON_SIZE);
        escBtn.setDepth(10);
        escBtn.setTint(getGlyphColor(alaIndex));
        escBtn.setInteractive({ useHandCursor: true });
        escBtn.on('pointerdown', () => this.closeScene());

        // ESC key
        const escKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
        escKey.on('down', () => this.closeScene());

        // --- 120-item grid ---
        const headerHeight = PADDING + BUTTON_SIZE + PADDING;

        // On mobile, spread glyphs across full viewport for easier tapping
        const hSpacing = this.mobile
            ? Math.floor((width - 2 * PADDING) / GLYPH_COLS)
            : GLYPH_SIZE + GLYPH_BUFFER;
        const availableHeight = height - headerHeight - PADDING;
        const vSpacing = this.mobile
            ? Math.floor(availableHeight / GLYPH_ROWS)
            : GLYPH_SIZE + GLYPH_BUFFER;
        const gridTotalWidth = (GLYPH_COLS - 1) * hSpacing + GLYPH_SIZE;
        const gridStartX = Math.floor((width - gridTotalWidth) / 2);
        const gridStartY = headerHeight + PADDING;

        // Wider hitbox on mobile to cover the full cell
        const hitWidth = this.mobile ? hSpacing : 10;
        const hitHeight = this.mobile ? vSpacing : 10;

        const glyphCount = getGlyphCount();
        for (let i = 0; i < glyphCount; i++) {
            const col = i % GLYPH_COLS;
            const row = Math.floor(i / GLYPH_COLS);
            const x = gridStartX + col * hSpacing;
            const y = gridStartY + row * vSpacing;

            // Start in glyph mode
            const img = this.add.image(x, y, 'toki_glyphs', i);
            img.setOrigin(0, 0);
            img.setDepth(5);
            img.setInteractive(
                new Phaser.Geom.Rectangle(-1, -1, hitWidth, hitHeight),
                Phaser.Geom.Rectangle.Contains,
            );
            img.input!.cursor = 'pointer';
            img.setTint(getGlyphColor(i));

            const idx = i;
            img.on('pointerdown', () => this.pickGlyph(idx));
            img.on('pointerover', () => {
                if (!this.spriteMode) img.setTint(0xffffff);
            });
            img.on('pointerout', () => {
                if (!this.spriteMode) img.setTint(getGlyphColor(idx));
            });

            this.gridItems.push(img);
        }
    }

    /** Update toggle button visual to reflect current mode. */
    private updateToggleAppearance(): void {
        if (this.spriteMode) {
            // Sprite mode active — bright highlight
            this.toggleBg.setFillStyle(0x226644);
            this.toggleBg.setStrokeStyle(1, 0x44cc88);
            this.toggleIcon.setTint(0x44cc88);
        } else {
            // Glyph mode (default) — subdued
            this.toggleBg.setFillStyle(0x444444);
            this.toggleBg.setStrokeStyle(1, 0x888888);
            this.toggleIcon.setTint(0xffffff);
        }
    }

    /** Swap all 120 grid cells between glyph and sprite mode. */
    private updateGrid(): void {
        const glyphCount = getGlyphCount();
        for (let i = 0; i < glyphCount; i++) {
            const img = this.gridItems[i];
            if (!img) continue;

            if (!this.spriteMode) {
                // Glyph mode — show toki pona glyph
                img.setTexture('toki_glyphs', i);
                img.setDisplaySize(GLYPH_SIZE, GLYPH_SIZE);
                img.setTint(getGlyphColor(i));
            } else {
                // Sprite mode — show object texture for (wordA, i)
                this.applySpriteTexture(img, this.wordA, i);
            }
        }
    }

    /** Set an image to the resolved sprite for a word pair. */
    private applySpriteTexture(img: Phaser.GameObjects.Image, indexA: number, indexB: number): void {
        // Starmap constellation for "mun" category
        if (indexA === MUN_INDEX && this.textures.exists('starmap')) {
            img.setTexture('starmap', indexB);
            img.setDisplaySize(GLYPH_SIZE, GLYPH_SIZE);
            img.clearTint();
            return;
        }

        const texKey = this.resolveTexture(indexA, indexB);
        if (texKey) {
            atlasApplyTexture(this, img, texKey);
            img.setDisplaySize(GLYPH_SIZE, GLYPH_SIZE);
            img.clearTint();
        } else {
            // No sprite — dim glyph fallback
            img.setTexture('toki_glyphs', indexB);
            img.setDisplaySize(GLYPH_SIZE, GLYPH_SIZE);
            img.setTint(0x333333);
        }
    }

    /** Resolve the texture key for a word pair, matching DictionaryScene logic. */
    private resolveTexture(indexA: number, indexB: number): string | null {
        // For multi-tile creatures, prefer the baby (8x8) sprite in the grid
        const multiId = getMultiTileCreatureId(indexA, indexB);
        if (multiId) {
            const babyKey = `rodent_${multiId}_baby`;
            if (this.textures.exists(babyKey)) return babyKey;
        }

        const wordA = getWord(indexA);
        const wordB = getWord(indexB);
        if (!wordA || !wordB) return null;

        const key = `${wordA} ${wordB}`;

        // SPRITE_MAP
        let texKey = SPRITE_MAP[key];
        if (texKey && this.textures.exists(texKey)) return texKey;

        // DEFINITION_SPRITE_MAP
        const def = DictionaryScene.lookupDef(indexA, indexB);
        texKey = DEFINITION_SPRITE_MAP[def] || '';
        if (texKey && this.textures.exists(texKey)) return texKey;

        // Dynamic tile_*/creature_* keys
        const underscored = key.replace(/ /g, '_');
        const tileKey = `tile_${underscored}`;
        const creatureKey = `creature_${underscored}`;
        if (this.textures.exists(tileKey)) return this.ensureTransparent(tileKey);
        if (this.textures.exists(creatureKey)) return this.ensureTransparent(creatureKey);
        if (isAtlasFrame(this, underscored)) return underscored;

        return null;
    }

    /** Make black pixels transparent for a raw texture, caching the result. */
    private ensureTransparent(rawKey: string): string {
        const processedKey = rawKey + '_dict_processed';
        if (this.textures.exists(processedKey)) return processedKey;

        const texture = this.textures.get(rawKey);
        if (!texture) return rawKey;
        const source = texture.getSourceImage() as HTMLImageElement;

        const canvas = document.createElement('canvas');
        canvas.width = source.width;
        canvas.height = source.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(source, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const threshold = 10;
        for (let i = 0; i < data.length; i += 4) {
            if (data[i] <= threshold && data[i + 1] <= threshold && data[i + 2] <= threshold) {
                data[i + 3] = 0;
            }
        }
        ctx.putImageData(imageData, 0, 0);
        this.textures.addCanvas(processedKey, canvas);
        return processedKey;
    }

    private pickGlyph(index: number): void {
        this.onSelect?.(index);
        this.scene.stop();
    }

    private closeScene(): void {
        this.onClose?.();
        this.scene.stop();
    }
}
